import { formatDate, localizeNumber } from "../../Helper/generalhelper";
import { valuesAreEquivalent } from "./scoopChangedFields";

const LINGUIST_INVOICE_STATUS = "invoice-linguist";
const CLIENT_INVOICE_STATUS = "invoice-client";

// Linguist-only keys used to catch mis-tagged Invoice-client logs from invoiceStatusApproved.
const LINGUIST_ONLY_KEYS = new Set([
  "is_approved",
  "job_id",
  "inv_due_date",
  "custom_invoice_no",
  "adjustment_amount",
  "job_total",
]);

// Labels from ViewInvoiceLinguist.js / LinguistInvoice.js. Do not reuse the client map.
const LINGUIST_FIELD_LABELS = {
  invoice_date: "Invoice Date",
  inv_due_date: "Due Date",
  custom_invoice_no: "Invoice #",
  invoice_number: "Invoice #",
  job_total: "Subtotal excl. Tax",
  item_total: "Subtotal excl. Tax",
  vat: "% TAX",
  vat2: "% TAX (In NOK)",
  adjustment_amount: "Additional/Missing Amount",
  adjustment_input_name: "Additional/Missing Amount",
  Invoice_cost: "Total Price",
  Invoice_cost2: "Total Price (In NOK)",
  paid_amount: "Paid Amount",
  paid_date: "Payment Date",
  invoice_status: "Invoice Status",
  is_approved: "Approved",
  job_id: "Job Description",
};

const DATE_KEYS = new Set(["invoice_date", "inv_due_date", "paid_date"]);
const MONEY_KEYS = new Set([
  "job_total",
  "item_total",
  "vat",
  "vat2",
  "adjustment_amount",
  "Invoice_cost",
  "Invoice_cost2",
  "paid_amount",
]);
const PAID_STATUSES = new Set(["Complete", "Completed", "Paid"]);

const PREVIEW_LIMIT = 4;

export function isLinguistInvoiceLog(logStatus, details) {
  const status = String(logStatus || "").trim().toLowerCase();
  if (status === LINGUIST_INVOICE_STATUS) return true;
  if (status !== CLIENT_INVOICE_STATUS) return false;

  const changedFields = parseChangedFields(details);
  if (!changedFields) return false;
  return Object.keys(changedFields).some(key => LINGUIST_ONLY_KEYS.has(key));
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  return false;
}

function isZeroDate(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim().startsWith("0000-00-00");
}

function valueForCompare(fieldKey, value) {
  if (DATE_KEYS.has(fieldKey) && isZeroDate(value)) return "";
  return value;
}

function isZeroOrEmptyNumber(value) {
  if (isEmptyValue(value)) return true;
  if (typeof value === "boolean") return false;
  const raw = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return false;
  return Number(raw) === 0;
}

function invoiceValuesAreEquivalent(fieldKey, oldValue, newValue) {
  if (DATE_KEYS.has(fieldKey)) {
    return formatDateValue(oldValue) === formatDateValue(newValue);
  }
  if (MONEY_KEYS.has(fieldKey) && isZeroOrEmptyNumber(oldValue) && isZeroOrEmptyNumber(newValue)) {
    return true;
  }
  return valuesAreEquivalent(valueForCompare(fieldKey, oldValue), valueForCompare(fieldKey, newValue), fieldKey);
}

function isApprovedTruthy(value) {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    return trimmed === "1" || trimmed === "true";
  }
  return false;
}

function formatMoney(value) {
  if (isEmptyValue(value)) return "";
  return String(localizeNumber(value));
}

function formatDateValue(value) {
  if (isEmptyValue(value) || isZeroDate(value)) return "";
  return formatDate(String(value).trim(), { includeTime: false });
}

function formatApproved(value) {
  if (isEmptyValue(value)) return "";
  if (isApprovedTruthy(value)) return "Approved";
  if (value === false || value === 0 || value === "0" || String(value).trim().toLowerCase() === "false") {
    return "Not yet approved";
  }
  return String(value);
}

function formatLinguistStatus(value, approvedValue) {
  if (isEmptyValue(value)) return "";
  const status = String(value).trim();
  if (PAID_STATUSES.has(status)) return "Paid";
  if (status === "Open") {
    return isApprovedTruthy(approvedValue) ? "Outstanding" : "Waiting on approval";
  }
  return status;
}

function formatJobIds(value) {
  const parsed = parseMaybeJson(value);
  if (isEmptyValue(parsed) || (Array.isArray(parsed) && !parsed.length)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      const id = row && typeof row === "object" ? row.id : row;
      if (isEmptyValue(id)) return "";
      return String(id).trim();
    })
    .filter(Boolean)
    .join("\n");
}

export function formatLinguistInvoiceFieldValue(fieldKey, value, context = {}) {
  if (fieldKey === "invoice_status") return formatLinguistStatus(value, context.approved);
  if (fieldKey === "is_approved") return formatApproved(value);
  if (fieldKey === "job_id") return formatJobIds(value);
  if (MONEY_KEYS.has(fieldKey)) return formatMoney(value);
  if (DATE_KEYS.has(fieldKey)) return formatDateValue(value);
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function getLinguistInvoiceChangedFields(details) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  const approvedChange = changedFields.is_approved;

  return Object.keys(changedFields)
    .filter(key => LINGUIST_FIELD_LABELS[key])
    .filter(key => {
      const change = changedFields[key];
      if (!change || typeof change !== "object" || Array.isArray(change)) return false;
      if (!Object.prototype.hasOwnProperty.call(change, "old") && !Object.prototype.hasOwnProperty.call(change, "new")) {
        return false;
      }
      return !invoiceValuesAreEquivalent(key, change.old, change.new);
    })
    .map(key => ({
      key,
      label: LINGUIST_FIELD_LABELS[key],
      old: formatLinguistInvoiceFieldValue(key, changedFields[key].old, {
        approved: key === "invoice_status" && approvedChange ? approvedChange.old : undefined,
      }),
      new: formatLinguistInvoiceFieldValue(key, changedFields[key].new, {
        approved: key === "invoice_status" && approvedChange ? approvedChange.new : undefined,
      }),
    }))
    .filter(change => change.old !== change.new);
}

export function getLinguistInvoiceChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
